import React, { useEffect, useRef, useState } from 'react';
import { validateFile } from '../../logics/validateFile';
import type { FileUploadPresentation } from '../../presentations';

type UploadedFile = React.ComponentProps<typeof FileUploadPresentation>['value'][number];
type DefaultFile = NonNullable<
  React.ComponentProps<typeof FileUploadPresentation>['defaultFiles']
>[number];

type BaseArgs = {
  type: 'image' | 'file';
  accept: string[];
  maxSizeMb: number;
};

type SingleArgs = BaseArgs & {
  isMultiple: false;
  value?: File;
  onChange: (file: File | undefined) => void;
  /**
   * 編集画面で既存ファイルを渡す場合（single のみ）。value が undefined のとき初期値として使用する。
   * - DefaultImageFile（url を持つ）: url を preview として使用する
   * - DefaultAttachmentFile（name のみ）: preview なし
   */
  defaultFile?: DefaultFile;
};

type MultipleArgs = BaseArgs & {
  isMultiple: true;
  value: File[];
  onChange: (files: File[]) => void;
};

type Args = SingleArgs | MultipleArgs;

type IUseFileUpload = (args: Args) => {
  state: {
    internalValue: UploadedFile[];
    isDragging: boolean;
    validationError?: string;
  };
  actions: {
    handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
    handleDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
    handleDrop: (e: React.DragEvent<HTMLDivElement>) => void;
    handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleRemove: (index: number) => void;
  };
  refs: {
    fileInputRef: React.RefObject<HTMLInputElement>;
  };
};

/**
 * FileUpload コンポーネントの状態・操作・File↔UploadedFile 変換をすべて管理する hook。
 *
 * コンポーネント外部は File / File[] ベースで値をやりとりし、
 * 内部では UploadedFile[] に変換して処理する。
 * drag&drop・file input・削除のハンドラ、およびバリデーションも本 hook に集約する。
 */
export const useFileUpload: IUseFileUpload = ({
  type,
  accept,
  maxSizeMb,
  isMultiple,
  value,
  onChange,
  defaultFile,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * File → UploadedFile のキャッシュ。
   * re-render のたびに ObjectURL が新規生成されるのを防ぐ。
   * File オブジェクトの参照をキーに、生成済みの UploadedFile を保持する。
   */
  const uploadedCacheRef = useRef<Map<File, UploadedFile>>(new Map());

  /**
   * single モードでユーザーが明示的に削除した状態を追跡する ref。
   * value が undefined に戻っても defaultFile が再表示されないよう制御する。
   */
  const isRemovedRef = useRef(false);

  /**
   * internalValue の最新参照を保持する ref。
   * useEffect の依存配列を空にしつつ最新値を参照するために使用する。
   */
  const internalValueRef = useRef<UploadedFile[]>([]);

  /**
   * File → UploadedFile 変換（キャッシュ優先）。
   * キャッシュがある場合はそれを返す。ない場合は生成してキャッシュに追加する。
   * type === 'image' のとき preview として ObjectURL を生成する。
   */
  const toUploadedFile = (file: File): UploadedFile => {
    if (uploadedCacheRef.current.has(file)) {
      return uploadedCacheRef.current.get(file)!;
    }
    const uploaded: UploadedFile = {
      name: file.name,
      sizeBytes: file.size,
      file,
      preview: type === 'image' ? URL.createObjectURL(file) : undefined,
    };
    uploadedCacheRef.current.set(file, uploaded);
    return uploaded;
  };

  /**
   * 外部から渡された File / File[] を内部で扱う UploadedFile[] に変換する。
   *
   * isMultiple === true の場合: File[] をそのまま UploadedFile[] に変換する。
   * isMultiple === false の場合:
   *   - value が存在する → toUploadedFile で変換し、削除フラグをリセットする
   *   - value が undefined かつ未削除かつ defaultFile がある → 初期値として表示する
   *     - DefaultImageFile（url を持つ）: url を preview として使用する
   *     - DefaultAttachmentFile（name のみ）: preview なし
   *   - それ以外（削除済みまたは value も defaultFile もない） → 空配列
   */
  const internalValue: UploadedFile[] = (() => {
    if (isMultiple) {
      const files = value as File[] | undefined;
      return (files ?? []).map(toUploadedFile);
    }

    const singleFile = value as File | undefined;
    if (singleFile != null) {
      // 新しいファイルが渡されたら削除フラグをリセットする
      isRemovedRef.current = false;
      return [toUploadedFile(singleFile)];
    }
    if (!isRemovedRef.current && defaultFile != null) {
      const isImageFile = 'url' in defaultFile;
      return [
        {
          name: isImageFile ? '' : defaultFile.name,
          sizeBytes: 0,
          url: isImageFile ? defaultFile.url : undefined,
          preview: isImageFile ? defaultFile.url : undefined,
        } satisfies UploadedFile,
      ];
    }
    return [];
  })();

  // internalValue の最新参照を ref に同期する（useEffect 内で参照するため）
  internalValueRef.current = internalValue;

  /**
   * UploadedFile[] を受け取り、File / File[] に変換して外部の onChange を呼び出す。
   *
   * isMultiple === true の場合: UploadedFile[] → File[] として onChange を呼ぶ。
   * isMultiple === false の場合:
   *   - ファイルが空（削除操作） → isRemovedRef を true にして onChange(undefined) を呼ぶ
   *   - ファイルがある → onChange(file) を呼ぶ
   */
  const internalOnChange = (files: UploadedFile[]) => {
    if (isMultiple) {
      (onChange as (value: File[]) => void)(files.map(f => f.file as File));
    } else {
      if (files.length === 0) {
        // ユーザーが削除ボタンを押した操作を記録する
        isRemovedRef.current = true;
      }
      (onChange as (value: File | undefined) => void)(files[0]?.file);
    }
  };

  /**
   * useEffect: アンマウント時にキャッシュ内の ObjectURL を revoke する。
   *
   * URL.createObjectURL で生成した blob: URL は、明示的に revokeObjectURL を呼ばないと
   * ページ離脱までブラウザメモリに残り続ける（GC 対象にならない）。
   * コンポーネントのライフサイクルに紐づくリソース解放であり、
   * イベントハンドラやレンダリングロジックでは代替できないため useEffect を使用する。
   * 依存配列は空（アンマウント時のみ実行）。最新の internalValue は internalValueRef 経由で取得する。
   */
  useEffect(() => {
    const cache = uploadedCacheRef.current;
    return () => {
      cache.forEach(uploaded => {
        if (uploaded.preview?.startsWith('blob:')) {
          URL.revokeObjectURL(uploaded.preview);
        }
      });
    };
  }, []);

  /**
   * File を1件受け取り、バリデーション → UploadedFile 変換 → onChange 通知を行う。
   * バリデーションエラーがあれば onChange を呼ばずに早期リターンする。
   */
  const handleFile = (file: File) => {
    const error = validateFile(file, accept, maxSizeMb);
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(undefined);

    const uploaded = toUploadedFile(file);

    if (!isMultiple) {
      internalOnChange([uploaded]);
    } else {
      internalOnChange([...internalValueRef.current, uploaded]);
    }
  };

  /**
   * ドラッグ中の状態を有効にする。
   * preventDefault でブラウザのデフォルト動作（ファイルを開く）を抑止する。
   */
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  /**
   * ドラッグ中の状態を解除する。
   * 子要素への移動による誤発火を currentTarget.contains で防いでいる。
   */
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragging(false);
  };

  /**
   * ドロップされたファイルを処理する。
   * 複数ファイルがドロップされた場合も先頭の1ファイルのみ処理する。
   */
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  };

  /**
   * input[type=file] の変更を処理する。
   * 選択後に value をリセットし、同じファイルの再選択を可能にする。
   */
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
      e.target.value = '';
    }
  };

  /**
   * 指定インデックスのファイルを削除する。
   * preview が blob: URL の場合は即時 revoke しメモリリークを防ぐ。
   * キャッシュに登録されていたファイルはキャッシュからも削除し、
   * アンマウント時の二重 revoke を防ぐ。
   */
  const handleRemove = (index: number) => {
    const current = internalValueRef.current;
    const removed = current[index];
    if (removed?.preview?.startsWith('blob:')) {
      URL.revokeObjectURL(removed.preview);
      if (removed.file != null) {
        uploadedCacheRef.current.delete(removed.file);
      }
    }
    internalOnChange(current.filter((_, i) => i !== index));
  };

  return {
    state: { internalValue, isDragging, validationError },
    actions: { handleDragOver, handleDragLeave, handleDrop, handleFileInputChange, handleRemove },
    refs: { fileInputRef },
  };
};
